<template>
  <div>
    <div class="admin-toolbar">
      <p class="admin-subtitle">
        Only approved suggestions appear on the public Suggestions page.
        <template v-if="pendingCount"> · <span class="font-semibold text-amber-700">{{ pendingCount }} pending</span></template>
      </p>
    </div>

    <div v-if="loading" class="admin-empty">Loading suggestions...</div>
    <div v-else-if="error" class="admin-empty text-red-600">{{ error }}</div>
    <div v-else-if="suggestions.length === 0" class="admin-empty">
      <i class="fa-solid fa-comment-dots text-2xl text-gray-300"></i>
      No suggestions found.
    </div>

    <div v-else class="admin-table-wrap">
      <table class="admin-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Suggestion</th>
            <th>Status</th>
            <th>Date</th>
            <th class="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="suggestion in suggestions" :key="suggestion.id">
            <td class="whitespace-nowrap">{{ suggestion.user_email }}</td>
            <td class="max-w-md whitespace-pre-wrap">{{ suggestion.suggestion_text }}</td>
            <td>
              <span
                :class="suggestion.is_approved ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'"
                class="badge"
              >
                {{ suggestion.is_approved ? 'Approved' : 'Pending' }}
              </span>
            </td>
            <td class="whitespace-nowrap text-gray-500">{{ formatDate(suggestion.created_at) }}</td>
            <td>
              <div class="flex justify-end gap-2">
                <button
                  v-if="!suggestion.is_approved"
                  @click="approveSuggestion(suggestion.id)"
                  class="btn-success btn-sm"
                >
                  Approve
                </button>
                <button @click="deleteSuggestion(suggestion.id)" class="btn-danger btn-sm">Delete</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import Swal from 'sweetalert2'
import api, { errorMessage } from '@/api'

const suggestions = ref([])
const loading = ref(false)
const error = ref(null)

const pendingCount = computed(() => suggestions.value.filter(s => !s.is_approved).length)

const fetchSuggestions = async () => {
  loading.value = true
  error.value = null
  try {
    // Sent with the admin token, so pending suggestions are included too
    const response = await api.get('/suggestions')
    suggestions.value = response.data
  } catch (err) {
    error.value = errorMessage(err, 'Failed to fetch suggestions')
  } finally {
    loading.value = false
  }
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

const approveSuggestion = async id => {
  const result = await Swal.fire({
    title: 'Approve Suggestion?',
    text: "It will be shown publicly on the Suggestions page.",
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#059669',
    cancelButtonColor: '#6B7280',
    confirmButtonText: 'Yes, approve it!'
  });

  if (!result.isConfirmed) return;

  try {
    await api.put(`/suggestions/${id}/approve`)
    const suggestion = suggestions.value.find(s => s.id === id)
    if (suggestion) suggestion.is_approved = 1
    Swal.fire({ icon: 'success', title: 'Approved!', text: 'Suggestion approved.', timer: 1600, showConfirmButton: false });
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: errorMessage(err, 'Failed to approve suggestion'), confirmButtonColor: '#BF3131' });
  }
}

const deleteSuggestion = async id => {
  const result = await Swal.fire({
    title: 'Delete Suggestion?',
    text: "You won't be able to revert this!",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#DC2626',
    cancelButtonColor: '#6B7280',
    confirmButtonText: 'Yes, delete it!'
  });

  if (!result.isConfirmed) return;

  try {
    await api.delete(`/suggestions/${id}`)
    suggestions.value = suggestions.value.filter(s => s.id !== id)
    Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Suggestion deleted.', timer: 1600, showConfirmButton: false });
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: errorMessage(err, 'Failed to delete suggestion'), confirmButtonColor: '#BF3131' });
  }
}

onMounted(fetchSuggestions)
</script>
